import React, { useMemo, useState } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    RadialBarChart,
    RadialBar,
    AreaChart,
    Area,
} from "recharts";
import {
    FaMoneyBillWave,
    FaStar,
    FaArrowUp,
    FaArrowDown,
    FaCheckCircle,
    FaClock,
    FaCalendarAlt,
    FaTimesCircle,
    FaWallet,
    FaUserNurse,
    FaUserMd,
    FaUserFriends,
    FaSearch,
    FaHandHoldingMedical,
    FaStethoscope,
    FaFlask,
    FaFileInvoiceDollar,
    FaChevronRight,
    FaHeartbeat,
    FaHospital,
    FaBolt,
} from "react-icons/fa";

/* =========================================================
   HELPERS
========================================================= */
const inr = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const cx = (...classes) => classes.filter(Boolean).join(" ");

const FILTERS = [
    { key: "live", label: "Live" },
    { key: "2days", label: "Last 2 Days" },
    { key: "7days", label: "Last 7 Days" },
    { key: "month", label: "Last Month" },
    { key: "overall", label: "Overall" },
];

const COLORS = ["#2563eb", "#14b8a6", "#f59e0b", "#8b5cf6", "#ef4444"];

/* =========================================================
   DUMMY DATA
========================================================= */
const dashboardByFilter = {
    live: {
        headerHint: "Real-time",
        kpis: {
            activePatients: { value: 128, deltaPct: 4.2, trend: [10, 14, 13, 16, 18, 20] },
            serviceCompletion: { pct: 84, done: 287, total: 342 },
            revenueToDate: { value: 315400, trend: [12, 14, 16, 15, 18, 19] },
            feedback: { score: 4.3, outOf: 5.0, reviews: 128 },
            pendingPayments: { value: 42000, deltaPct: -1.8, trend: [18, 20, 17, 16, 14, 13] },
            providerPayout: { value: 110000, deltaPct: 2.5, trend: [10, 11, 10, 12, 12, 13] },
        },
        statusCounts: {
            ongoing: 18,
            completed: 120,
            upcoming: 25,
            cancelled: 6,
            paymentPending: 14,
        },
        roleVisits: [
            { name: "10 AM", nurse: 12, physio: 6, assistant: 8 },
            { name: "12 PM", nurse: 15, physio: 8, assistant: 10 },
            { name: "2 PM", nurse: 18, physio: 7, assistant: 12 },
            { name: "4 PM", nurse: 14, physio: 9, assistant: 11 },
        ],
        revenueTrend: [
            { name: "10 AM", revenue: 12000 },
            { name: "12 PM", revenue: 15000 },
            { name: "2 PM", revenue: 17000 },
            { name: "4 PM", revenue: 19000 },
        ],
        collections: [
            { name: "Hospital Share", value: 145500 },
            { name: "Provider Payout", value: 97040 },
            { name: "Commission", value: 18430 },
            { name: "Pending", value: 42000 },
        ],
        services: [
            { category: "Nursing Care", icon: "nursing", visits: 120, revenue: 82400, rating: 4.7 },
            { category: "Physiotherapy", icon: "physio", visits: 85, revenue: 61200, rating: 4.6 },
            { category: "Doctor Consultation", icon: "doctor", visits: 42, revenue: 38600, rating: 4.5 },
            { category: "Lab Services", icon: "lab", visits: 30, revenue: 28900, rating: 4.4 },
            { category: "Medical Equipment", icon: "equipment", visits: 18, revenue: 15800, rating: 4.3 },
        ],
        visits: {
            ongoing: [
                { patient: "Rater Smith", address: "[Simplified Address]", staff: "Nurse Jomi", time: "10:30 AM", status: "Ongoing" },
                { patient: "John Doe", address: "[Simplified Address]", staff: "Physio Arun", time: "11:15 AM", status: "Ongoing" },
            ],
            completed: [
                { patient: "Priya Sharma", address: "[Simplified Address]", staff: "Nurse Diya", time: "09:20 AM", status: "Completed" },
            ],
            upcoming: [
                { patient: "Anil Kumar", address: "[Simplified Address]", staff: "Nurse Jomi", time: "05:00 PM", status: "Upcoming" },
            ],
            cancelled: [
                { patient: "Meena Iyer", address: "[Simplified Address]", staff: "Physio Arun", time: "01:00 PM", status: "Cancelled" },
            ],
        },
        patients: [
            { name: "Rater Smith", age: 58, diagnosis: "Hypertension", assigned: "Nurse Jomi", visits: 8, payment: "Paid" },
            { name: "John Doe", age: 64, diagnosis: "Post Surgery Care", assigned: "Physio Arun", visits: 5, payment: "Pending" },
            { name: "Priya Sharma", age: 47, diagnosis: "Diabetes", assigned: "Nurse Diya", visits: 12, payment: "Overdue" },
        ],
        staff: [
            { name: "Nurse Jomi", role: "Nursing", completed: 28, rating: 4.8, payout: 18600 },
            { name: "Nurse Diya", role: "Nursing", completed: 25, rating: 4.6, payout: 16400 },
            { name: "Physio Arun", role: "Physio", completed: 21, rating: 4.7, payout: 14100 },
            { name: "Nurse Diya (Asst.)", role: "Assistant", completed: 18, rating: 4.5, payout: 11900 },
        ],
        payments: {
            paid: 280000,
            pending: 42000,
            overdue: 18000,
            payoutProcessed: 97040,
            payoutPending: 14500,
            recent: [
                { ref: "PAY-10231", patient: "Rater Smith", amount: 5200, status: "Paid", date: "Today" },
                { ref: "PAY-10232", patient: "John Doe", amount: 6800, status: "Pending", date: "Today" },
                { ref: "PAY-10212", patient: "Priya Sharma", amount: 4300, status: "Overdue", date: "Yesterday" },
            ],
        },
        reviews: [
            { patient: "Rater Smith", score: 5, note: "Timely visit and excellent care.", date: "Today" },
            { patient: "John Doe", score: 4, note: "Good service. Slight delay.", date: "Today" },
            { patient: "Priya Sharma", score: 5, note: "Very professional staff.", date: "Yesterday" },
        ],
    },

    "2days": {
        headerHint: "Last 2 Days",
        kpis: {
            activePatients: { value: 242, deltaPct: 3.1, trend: [18, 19, 21, 20, 22, 24] },
            serviceCompletion: { pct: 81, done: 520, total: 642 },
            revenueToDate: { value: 148000, trend: [14, 15, 14, 16, 17, 18] },
            feedback: { score: 4.2, outOf: 5.0, reviews: 240 },
            pendingPayments: { value: 56000, deltaPct: 2.2, trend: [15, 16, 18, 19, 20, 22] },
            providerPayout: { value: 56000, deltaPct: 1.1, trend: [10, 10, 11, 12, 11, 12] },
        },
        statusCounts: { ongoing: 22, completed: 260, upcoming: 40, cancelled: 10, paymentPending: 20 },
        roleVisits: [
            { name: "Day 1", nurse: 20, physio: 12, assistant: 15 },
            { name: "Day 2", nurse: 22, physio: 10, assistant: 18 },
        ],
        revenueTrend: [
            { name: "Day 1", revenue: 72000 },
            { name: "Day 2", revenue: 76000 },
        ],
        collections: [
            { name: "Hospital Share", value: 88800 },
            { name: "Provider Payout", value: 51800 },
            { name: "Commission", value: 7400 },
            { name: "Pending", value: 56000 },
        ],
        services: [
            { category: "Nursing Care", icon: "nursing", visits: 210, revenue: 112000, rating: 4.6 },
            { category: "Physiotherapy", icon: "physio", visits: 120, revenue: 76000, rating: 4.5 },
            { category: "Doctor Consultation", icon: "doctor", visits: 80, revenue: 52000, rating: 4.4 },
            { category: "Lab Services", icon: "lab", visits: 60, revenue: 36000, rating: 4.3 },
            { category: "Medical Equipment", icon: "equipment", visits: 25, revenue: 18000, rating: 4.2 },
        ],
        visits: { ongoing: [], completed: [], upcoming: [], cancelled: [] },
        patients: [],
        staff: [],
        payments: { paid: 132000, pending: 56000, overdue: 21000, payoutProcessed: 51800, payoutPending: 19000, recent: [] },
        reviews: [],
    },

    "7days": {
        headerHint: "Last 7 Days",
        kpis: {
            activePatients: { value: 680, deltaPct: 5.8, trend: [18, 20, 22, 21, 24, 26] },
            serviceCompletion: { pct: 86, done: 1920, total: 2230 },
            revenueToDate: { value: 315400, trend: [12, 13, 14, 15, 16, 17] },
            feedback: { score: 4.3, outOf: 5.0, reviews: 840 },
            pendingPayments: { value: 42000, deltaPct: -0.8, trend: [16, 15, 15, 14, 13, 12] },
            providerPayout: { value: 97040, deltaPct: 3.7, trend: [9, 10, 11, 11, 12, 12] },
        },
        statusCounts: { ongoing: 30, completed: 920, upcoming: 80, cancelled: 22, paymentPending: 34 },
        roleVisits: [
            { name: "Mon", nurse: 40, physio: 25, assistant: 30 },
            { name: "Tue", nurse: 38, physio: 20, assistant: 28 },
            { name: "Wed", nurse: 42, physio: 22, assistant: 35 },
            { name: "Thu", nurse: 45, physio: 24, assistant: 36 },
            { name: "Fri", nurse: 48, physio: 28, assistant: 38 },
            { name: "Sat", nurse: 35, physio: 18, assistant: 25 },
            { name: "Sun", nurse: 30, physio: 15, assistant: 20 },
        ],
        revenueTrend: [
            { name: "Mon", revenue: 40000 },
            { name: "Tue", revenue: 38000 },
            { name: "Wed", revenue: 42000 },
            { name: "Thu", revenue: 45000 },
            { name: "Fri", revenue: 48000 },
            { name: "Sat", revenue: 35000 },
            { name: "Sun", revenue: 30000 },
        ],
        collections: [
            { name: "Hospital Share", value: 145500 },
            { name: "Provider Payout", value: 97040 },
            { name: "Commission", value: 18430 },
            { name: "Pending", value: 42000 },
        ],
        services: [
            { category: "Nursing Care", icon: "nursing", visits: 420, revenue: 82400, rating: 4.7 },
            { category: "Physiotherapy", icon: "physio", visits: 310, revenue: 61200, rating: 4.6 },
            { category: "Doctor Consultation", icon: "doctor", visits: 180, revenue: 38600, rating: 4.5 },
            { category: "Lab Services", icon: "lab", visits: 110, revenue: 28900, rating: 4.4 },
            { category: "Medical Equipment", icon: "equipment", visits: 60, revenue: 15800, rating: 4.3 },
        ],
        visits: { ongoing: [], completed: [], upcoming: [], cancelled: [] },
        patients: [],
        staff: [
            { name: "Nurse Jomi", role: "Nursing", completed: 128, rating: 4.8, payout: 48600 },
            { name: "Nurse Diya", role: "Nursing", completed: 116, rating: 4.6, payout: 44400 },
            { name: "Physio Arun", role: "Physio", completed: 98, rating: 4.7, payout: 39800 },
            { name: "Asst. Ravi", role: "Assistant", completed: 84, rating: 4.5, payout: 28000 },
        ],
        payments: {
            paid: 280000,
            pending: 42000,
            overdue: 18000,
            payoutProcessed: 97040,
            payoutPending: 14500,
            recent: [],
        },
        reviews: [],
    },

    month: {
        headerHint: "Last Month",
        kpis: {
            activePatients: { value: 2400, deltaPct: 2.2, trend: [18, 19, 19, 20, 21, 21] },
            serviceCompletion: { pct: 83, done: 8200, total: 9900 },
            revenueToDate: { value: 700000, trend: [12, 13, 15, 14, 16, 18] },
            feedback: { score: 4.2, outOf: 5.0, reviews: 3200 },
            pendingPayments: { value: 112000, deltaPct: 1.4, trend: [16, 16, 17, 18, 19, 20] },
            providerPayout: { value: 245000, deltaPct: 1.9, trend: [10, 10, 11, 11, 12, 12] },
        },
        statusCounts: { ongoing: 55, completed: 3600, upcoming: 210, cancelled: 90, paymentPending: 120 },
        roleVisits: [
            { name: "Week 1", nurse: 150, physio: 90, assistant: 120 },
            { name: "Week 2", nurse: 180, physio: 100, assistant: 140 },
            { name: "Week 3", nurse: 170, physio: 110, assistant: 130 },
            { name: "Week 4", nurse: 200, physio: 120, assistant: 160 },
        ],
        revenueTrend: [
            { name: "Week 1", revenue: 150000 },
            { name: "Week 2", revenue: 180000 },
            { name: "Week 3", revenue: 170000 },
            { name: "Week 4", revenue: 200000 },
        ],
        collections: [
            { name: "Hospital Share", value: 420000 },
            { name: "Provider Payout", value: 245000 },
            { name: "Commission", value: 35000 },
            { name: "Pending", value: 112000 },
        ],
        services: [
            { category: "Nursing Care", icon: "nursing", visits: 1680, revenue: 284000, rating: 4.6 },
            { category: "Physiotherapy", icon: "physio", visits: 1040, revenue: 190000, rating: 4.5 },
            { category: "Doctor Consultation", icon: "doctor", visits: 720, revenue: 120000, rating: 4.4 },
            { category: "Lab Services", icon: "lab", visits: 420, revenue: 70000, rating: 4.3 },
            { category: "Medical Equipment", icon: "equipment", visits: 210, revenue: 36000, rating: 4.2 },
        ],
        visits: { ongoing: [], completed: [], upcoming: [], cancelled: [] },
        patients: [],
        staff: [],
        payments: { paid: 588000, pending: 112000, overdue: 42000, payoutProcessed: 245000, payoutPending: 38000, recent: [] },
        reviews: [],
    },

    overall: {
        headerHint: "Overall",
        kpis: {
            activePatients: { value: 22000, deltaPct: 1.2, trend: [18, 18, 19, 19, 20, 20] },
            serviceCompletion: { pct: 85, done: 120000, total: 141000 },
            revenueToDate: { value: 3154000, trend: [12, 12, 13, 14, 15, 16] },
            feedback: { score: 4.3, outOf: 5.0, reviews: 42000 },
            pendingPayments: { value: 320000, deltaPct: -0.4, trend: [16, 16, 15, 15, 14, 14] },
            providerPayout: { value: 1100000, deltaPct: 0.9, trend: [10, 10, 10, 11, 11, 11] },
        },
        statusCounts: { ongoing: 90, completed: 90000, upcoming: 600, cancelled: 280, paymentPending: 420 },
        roleVisits: [
            { name: "Jan", nurse: 600, physio: 400, assistant: 500 },
            { name: "Feb", nurse: 650, physio: 420, assistant: 520 },
            { name: "Mar", nurse: 700, physio: 450, assistant: 550 },
            { name: "Apr", nurse: 750, physio: 480, assistant: 600 },
        ],
        revenueTrend: [
            { name: "Jan", revenue: 600000 },
            { name: "Feb", revenue: 650000 },
            { name: "Mar", revenue: 700000 },
            { name: "Apr", revenue: 750000 },
        ],
        collections: [
            { name: "Hospital Share", value: 1890000 },
            { name: "Provider Payout", value: 1100000 },
            { name: "Commission", value: 154000 },
            { name: "Pending", value: 320000 },
        ],
        services: [
            { category: "Nursing Care", icon: "nursing", visits: 42000, revenue: 1200000, rating: 4.6 },
            { category: "Physiotherapy", icon: "physio", visits: 28000, revenue: 900000, rating: 4.5 },
            { category: "Doctor Consultation", icon: "doctor", visits: 18000, revenue: 650000, rating: 4.4 },
            { category: "Lab Services", icon: "lab", visits: 12000, revenue: 300000, rating: 4.3 },
            { category: "Medical Equipment", icon: "equipment", visits: 6000, revenue: 104000, rating: 4.2 },
        ],
        visits: { ongoing: [], completed: [], upcoming: [], cancelled: [] },
        patients: [],
        staff: [],
        payments: { paid: 2834000, pending: 320000, overdue: 140000, payoutProcessed: 1100000, payoutPending: 80000, recent: [] },
        reviews: [],
    },
};

/* =========================================================
   MAIN COMPONENT
========================================================= */
const PartnerDashboard = () => {
    const [filter, setFilter] = useState("live");
    const [visitTab, setVisitTab] = useState("ongoing");
    const [patientSearch, setPatientSearch] = useState("");

    const d = dashboardByFilter[filter];

    const totals = useMemo(() => {
        const roleTotal =
            d.roleVisits.reduce((acc, r) => acc + r.nurse + r.physio + r.assistant, 0) || 0;

        return {
            roleTotal,
            hospitalShare: d.collections.find((x) => x.name === "Hospital Share")?.value || 0,
            providerPayout: d.collections.find((x) => x.name === "Provider Payout")?.value || 0,
            commission: d.collections.find((x) => x.name === "Commission")?.value || 0,
            pending: d.collections.find((x) => x.name === "Pending")?.value || 0,
        };
    }, [d]);

    const filteredPatients = useMemo(() => {
        const list = d.patients?.length ? d.patients : dashboardByFilter.live.patients;
        return list.filter((p) =>
            `${p.name} ${p.diagnosis} ${p.assigned}`.toLowerCase().includes(patientSearch.toLowerCase())
        );
    }, [d.patients, patientSearch]);

    const visitsForTable = useMemo(() => {
        const v = d.visits?.[visitTab];
        if (v && v.length) return v;
        return dashboardByFilter.live.visits[visitTab] || [];
    }, [d.visits, visitTab]);

    return (
        <div
            className="h-[86vh] overflow-y-auto rounded-[28px] border border-slate-200 bg-slate-50 shadow-[0_20px_60px_rgba(15,23,42,0.08)] m-2"
            style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}
        >
            <div className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl">
                <div className="px-6 md:px-8 py-5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-blue-700 uppercase">
                            <FaBolt className="text-[10px]" />
                            {d.headerHint} operations view
                        </div>
                        <div className="mt-3 flex items-start gap-3">
                            <div className="h-12 w-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-md">
                                <FaHospital />
                            </div>
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight">
                                    Home Care Ops Dashboard
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Track visits, care performance, collections, payouts, and patient service quality.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={cx(
                                    "px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200",
                                    filter === f.key
                                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                        : "bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="px-6 md:px-8 pb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <TopMetaPill label="Active Range" value={d.headerHint} icon={<FaCalendarAlt />} />
                    <TopMetaPill label="Completion" value={`${d.kpis.serviceCompletion.pct}%`} icon={<FaCheckCircle />} />
                    <TopMetaPill label="Revenue" value={inr(d.kpis.revenueToDate.value)} icon={<FaMoneyBillWave />} />
                    <TopMetaPill label="Satisfaction" value={`${d.kpis.feedback.score}/5`} icon={<FaHeartbeat />} />
                </div>
            </div>

            <div className="p-6 md:p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                    <KpiPatients
                        title="Active Patients Today"
                        value={d.kpis.activePatients.value}
                        deltaPct={d.kpis.activePatients.deltaPct}
                        series={d.kpis.activePatients.trend}
                    />
                    <KpiCompletionPulse
                        title="Service Completion Pulse"
                        pct={d.kpis.serviceCompletion.pct}
                        done={d.kpis.serviceCompletion.done}
                        total={d.kpis.serviceCompletion.total}
                    />
                    <KpiRevenue
                        title="Revenue To-Date"
                        subtitle="Current selected period"
                        value={d.kpis.revenueToDate.value}
                        series={d.kpis.revenueToDate.trend}
                    />
                    <KpiFeedback
                        title="Client Feedback Score"
                        score={d.kpis.feedback.score}
                        outOf={d.kpis.feedback.outOf}
                        reviews={d.kpis.feedback.reviews}
                    />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                    <StatusCard icon={<FaClock />} label="Ongoing" value={d.statusCounts.ongoing} tone="blue" />
                    <StatusCard icon={<FaCheckCircle />} label="Completed" value={d.statusCounts.completed} tone="green" />
                    <StatusCard icon={<FaCalendarAlt />} label="Upcoming" value={d.statusCounts.upcoming} tone="amber" />
                    <StatusCard icon={<FaTimesCircle />} label="Cancelled" value={d.statusCounts.cancelled} tone="red" />
                    <StatusCard icon={<FaWallet />} label="Payment Pending" value={d.statusCounts.paymentPending} tone="violet" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <MoneyCard
                        icon={<FaMoneyBillWave />}
                        title="Hospital Share"
                        value={totals.hospitalShare}
                        helper="Share of total collections"
                        positive
                    />
                    <MoneyCard
                        icon={<FaHandHoldingMedical />}
                        title="Provider Payout"
                        value={totals.providerPayout}
                        helper="Total payable to field staff"
                        positive
                    />
                    <MoneyCard
                        icon={<FaFileInvoiceDollar />}
                        title="Commission"
                        value={totals.commission}
                        helper="Operational platform earnings"
                        positive={false}
                    />
                    <MoneyCard
                        icon={<FaWallet />}
                        title="Pending Amount"
                        value={totals.pending}
                        helper="Awaiting patient-side clearance"
                        positive={false}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <IconSummary
                        icon={<FaUserNurse />}
                        label="Nurse Visits"
                        value={d.roleVisits.reduce((a, x) => a + x.nurse, 0)}
                        subtitle="Primary bedside care visits"
                        tone="blue"
                    />
                    <IconSummary
                        icon={<FaUserMd />}
                        label="Physio Visits"
                        value={d.roleVisits.reduce((a, x) => a + x.physio, 0)}
                        subtitle="Therapy and recovery sessions"
                        tone="emerald"
                    />
                    <IconSummary
                        icon={<FaUserFriends />}
                        label="Assistant Visits"
                        value={d.roleVisits.reduce((a, x) => a + x.assistant, 0)}
                        subtitle="Support and routine assistance"
                        tone="amber"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card
                        title="Live & Recent Visits Tracking"
                        subtitle="Monitor running, completed, scheduled, and cancelled visits in one place."
                        right={
                            <div className="flex items-center gap-2 flex-wrap">
                                <TabButton active={visitTab === "ongoing"} onClick={() => setVisitTab("ongoing")}>
                                    Live
                                </TabButton>
                                <TabButton active={visitTab === "completed"} onClick={() => setVisitTab("completed")}>
                                    Completed
                                </TabButton>
                                <TabButton active={visitTab === "upcoming"} onClick={() => setVisitTab("upcoming")}>
                                    Upcoming
                                </TabButton>
                                <TabButton active={visitTab === "cancelled"} onClick={() => setVisitTab("cancelled")}>
                                    Cancelled
                                </TabButton>
                            </div>
                        }
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b border-slate-200 text-slate-500">
                                        <th className="py-3 font-medium">Patient</th>
                                        <th className="font-medium">Address</th>
                                        <th className="font-medium">Assigned Staff</th>
                                        <th className="font-medium">Start Time</th>
                                        <th className="font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visitsForTable.length ? (
                                        visitsForTable.map((v, idx) => (
                                            <tr key={idx} className="border-b border-slate-100 last:border-none">
                                                <td className="py-3 font-semibold text-slate-700">{v.patient}</td>
                                                <td className="text-slate-500">{v.address}</td>
                                                <td className="text-slate-700">{v.staff}</td>
                                                <td className="text-slate-700">{v.time}</td>
                                                <td><Badge type={v.status} /></td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="py-6 text-slate-500" colSpan={5}>
                                                No visits in this tab for current range.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-sm font-semibold text-slate-700">Revenue movement</p>
                                    <p className="text-xs text-slate-500">Quick trend for the selected range</p>
                                </div>
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600">
                                    View trend <FaChevronRight className="text-[10px]" />
                                </span>
                            </div>
                            <div className="h-[150px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={d.revenueTrend}>
                                        <defs>
                                            <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                                                <stop offset="100%" stopColor="#2563eb" stopOpacity={0.03} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
                                        <YAxis hide />
                                        <Tooltip formatter={(v) => inr(v)} />
                                        <Area type="monotone" dataKey="revenue" stroke="#2563eb" fill="url(#revArea)" strokeWidth={2.5} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </Card>

                    <Card
                        title="Patient Management"
                        subtitle="Search patients by diagnosis, staff assignment, or visit ownership."
                        right={
                            <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 bg-slate-50">
                                <FaSearch className="text-slate-400 text-xs" />
                                <input
                                    value={patientSearch}
                                    onChange={(e) => setPatientSearch(e.target.value)}
                                    placeholder="Search patient / diagnosis / staff..."
                                    className="outline-none text-sm w-[240px] max-w-[45vw] bg-transparent"
                                />
                            </div>
                        }
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b border-slate-200 text-slate-500">
                                        <th className="py-3 font-medium">Patient</th>
                                        <th className="font-medium">Age</th>
                                        <th className="font-medium">Diagnosis</th>
                                        <th className="font-medium">Assigned</th>
                                        <th className="font-medium">Visits</th>
                                        <th className="font-medium">Payment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPatients.map((p, idx) => (
                                        <tr key={idx} className="border-b border-slate-100 last:border-none">
                                            <td className="py-3 font-semibold text-slate-700">{p.name}</td>
                                            <td>{p.age}</td>
                                            <td className="text-slate-600">{p.diagnosis}</td>
                                            <td>{p.assigned}</td>
                                            <td>{p.visits}</td>
                                            <td><Badge type={p.payment} /></td>
                                        </tr>
                                    ))}
                                    {!filteredPatients.length && (
                                        <tr>
                                            <td className="py-6 text-slate-500" colSpan={6}>
                                                No patients match your search.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card
                        title="Visit Role Analytics"
                        subtitle="Compare nurse, physio, and assistant visit distribution."
                        className="lg:col-span-2"
                    >
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={d.roleVisits}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: "12px" }} />
                                <Bar dataKey="nurse" fill="#2563eb" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="physio" fill="#14b8a6" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="assistant" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card
                        title="Collection Distribution"
                        subtitle="Breakdown of current period collections and pending flow."
                    >
                        <div className="h-[280px] mt-2">
                            <ResponsiveContainer width="100%" height="75%">
                                <PieChart>
                                    <Pie
                                        data={d.collections}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="48%"
                                        outerRadius={86}
                                        innerRadius={58}
                                        paddingAngle={3}
                                    >
                                        {d.collections.map((_, idx) => (
                                            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => inr(v)} />
                                </PieChart>
                            </ResponsiveContainer>

                            <div className="grid grid-cols-2 gap-3 text-xs">
                                {d.collections.map((c, idx) => (
                                    <div key={c.name} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                                            <span className="text-slate-500">{c.name}</span>
                                        </div>
                                        <div className="mt-1 text-sm font-semibold text-slate-700">{inr(c.value)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card
                        title="Service Categories & Performance"
                        subtitle="Contribution, revenue, and average satisfaction by service type."
                        className="lg:col-span-2"
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b border-slate-200 text-slate-500">
                                        <th className="py-3 font-medium">Service</th>
                                        <th className="text-center font-medium">Visits</th>
                                        <th className="text-right font-medium">Revenue</th>
                                        <th className="text-center font-medium">Rating</th>
                                        <th className="text-right font-medium">Contribution</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.services.map((s, idx) => {
                                        const share = totals.roleTotal > 0 ? Math.round((s.visits / totals.roleTotal) * 100) : 0;
                                        return (
                                            <tr key={idx} className="border-b border-slate-100 last:border-none">
                                                <td className="py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                                            <ServiceIcon type={s.icon} />
                                                        </div>
                                                        <div>
                                                            <div className="font-semibold text-slate-700">{s.category}</div>
                                                            <div className="text-xs text-slate-500">Operational service line</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center font-medium">{s.visits}</td>
                                                <td className="text-right font-medium">{inr(s.revenue)}</td>
                                                <td className="text-center">
                                                    <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                                                        {s.rating.toFixed(1)}
                                                        <FaStar className="text-yellow-400 text-[11px]" />
                                                    </span>
                                                </td>
                                                <td className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <div className="w-28 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                                            <div className="h-2.5 bg-blue-600 rounded-full" style={{ width: `${Math.min(100, share)}%` }} />
                                                        </div>
                                                        <span className="text-slate-500 text-xs">{share}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    <Card
                        title="Top Performers"
                        subtitle="Highest-output staff in the selected period."
                    >
                        <div className="space-y-3">
                            {(d.staff?.length ? d.staff : dashboardByFilter.live.staff).slice(0, 5).map((s, idx) => (
                                <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                                                {idx + 1}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-slate-700 truncate">{s.name}</div>
                                                <div className="text-xs text-slate-500">{s.role}</div>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-sm font-semibold text-slate-700">{s.completed}</div>
                                            <div className="text-xs text-slate-500">Completed</div>
                                        </div>
                                    </div>

                                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                            <FaStar />
                                            {s.rating.toFixed(1)}
                                        </span>
                                        <span className="font-semibold text-slate-700">{inr(s.payout)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 rounded-2xl bg-blue-600 p-4 text-white">
                            <div className="text-xs uppercase tracking-wide text-blue-100">Top performer</div>
                            <div className="text-lg font-semibold mt-1">
                                {(d.staff?.length ? d.staff : dashboardByFilter.live.staff)[0]?.name || "—"}
                            </div>
                            <div className="text-sm text-blue-50 mt-1">
                                Best-rated and highest-output professional in this range.
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card
                        title="Payments & Payout Tracking"
                        subtitle="Financial status of patient collections and provider settlement."
                    >
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <MiniMoneyBox label="Paid" value={d.payments.paid} tone="success" />
                            <MiniMoneyBox label="Pending" value={d.payments.pending} tone="warning" />
                            <MiniMoneyBox label="Overdue" value={d.payments.overdue} tone="danger" />
                            <MiniMoneyBox label="Payout Processed" value={d.payments.payoutProcessed} tone="primary" />
                            <MiniMoneyBox label="Payout Pending" value={d.payments.payoutPending} tone="muted" />
                            <MiniMoneyBox label="Pending Summary" value={totals.pending} tone="warning" />
                        </div>

                        <div className="mt-5 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b border-slate-200 text-slate-500">
                                        <th className="py-3 font-medium">Ref</th>
                                        <th className="font-medium">Patient</th>
                                        <th className="text-right font-medium">Amount</th>
                                        <th className="font-medium">Status</th>
                                        <th className="font-medium">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(d.payments.recent?.length ? d.payments.recent : dashboardByFilter.live.payments.recent).map(
                                        (p, idx) => (
                                            <tr key={idx} className="border-b border-slate-100 last:border-none">
                                                <td className="py-3 font-medium text-slate-700">{p.ref}</td>
                                                <td>{p.patient}</td>
                                                <td className="text-right font-medium">{inr(p.amount)}</td>
                                                <td><Badge type={p.status} /></td>
                                                <td className="text-slate-500">{p.date}</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    <Card
                        title="Recent Reviews & Feedback"
                        subtitle="Latest patient feedback and service satisfaction notes."
                    >
                        <div className="space-y-3">
                            {(d.reviews?.length ? d.reviews : dashboardByFilter.live.reviews).map((r, idx) => (
                                <div key={idx} className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-semibold text-slate-700">{r.patient}</div>
                                        <div className="inline-flex items-center gap-1 text-xs">
                                            <span className="font-semibold text-slate-700">{r.score.toFixed(1)}</span>
                                            <FaStar className="text-yellow-400 text-[11px]" />
                                            <span className="text-slate-300">•</span>
                                            <span className="text-slate-500">{r.date}</span>
                                        </div>
                                    </div>
                                    <div className="text-sm text-slate-600 mt-2 leading-6">{r.note}</div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   REUSABLE UI
========================================================= */
const Card = ({ title, subtitle, right, children, className }) => (
    <div
        className={cx(
            "bg-white rounded-[24px] border border-slate-200 shadow-[0_10px_30px_rgba(15,23,42,0.05)] p-5 md:p-6",
            className
        )}
    >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
            <div>
                <h3 className="font-bold text-slate-700 text-base md:text-lg">{title}</h3>
                {subtitle ? <p className="text-sm text-slate-500 mt-1">{subtitle}</p> : null}
            </div>
            {right ? <div className="shrink-0">{right}</div> : null}
        </div>
        {children}
    </div>
);

const TopMetaPill = ({ label, value, icon }) => (
    <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-white border border-blue-100 text-blue-600 flex items-center justify-center shadow-sm">
            {icon}
        </div>
        <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-sm font-semibold text-slate-700 truncate">{value}</div>
        </div>
    </div>
);

const TabButton = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        className={cx(
            "px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200",
            active
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
        )}
    >
        {children}
    </button>
);

const Badge = ({ type }) => {
    const map = {
        Ongoing: "bg-blue-50 text-blue-700 border-blue-100",
        Completed: "bg-green-50 text-green-700 border-green-100",
        Upcoming: "bg-amber-50 text-amber-700 border-amber-100",
        Cancelled: "bg-red-50 text-red-700 border-red-100",
        Paid: "bg-green-50 text-green-700 border-green-100",
        Pending: "bg-amber-50 text-amber-700 border-amber-100",
        Overdue: "bg-red-50 text-red-700 border-red-100",
    };

    return (
        <span className={cx("px-2.5 py-1 rounded-full border text-xs font-medium", map[type] || "bg-slate-50 text-slate-700 border-slate-100")}>
            {type}
        </span>
    );
};

const toneStyles = {
    blue: "bg-blue-600 text-white",
    green: "bg-green-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-600 text-white",
    violet: "bg-violet-600 text-white",
};

const StatusCard = ({ icon, label, value, tone = "blue" }) => (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                <p className="text-2xl font-bold text-slate-700 mt-2">{value}</p>
                <p className="text-xs text-slate-400 mt-1">Operational count</p>
            </div>
            <div className={cx("h-11 w-11 rounded-2xl flex items-center justify-center shadow-sm", toneStyles[tone])}>
                {icon}
            </div>
        </div>
    </div>
);

const MoneyCard = ({ icon, title, value, helper, positive }) => (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div className="h-12 w-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
                {icon}
            </div>
            <div
                className={cx(
                    "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold",
                    positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                )}
            >
                {positive ? <FaArrowUp /> : <FaArrowDown />}
                <span>{positive ? "+2.4%" : "-1.2%"}</span>
            </div>
        </div>

        <p className="text-sm text-slate-500 mt-4">{title}</p>
        <p className="text-2xl font-bold text-slate-700 mt-1 tracking-tight">{inr(value)}</p>
        <p className="text-xs text-slate-400 mt-2">{helper}</p>
    </div>
);

const IconSummary = ({ icon, label, value, subtitle, tone = "blue" }) => {
    const bgMap = {
        blue: "bg-blue-50 text-blue-600",
        emerald: "bg-emerald-50 text-emerald-700",
        amber: "bg-amber-50 text-amber-700",
    };

    return (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className={cx("h-12 w-12 rounded-2xl flex items-center justify-center text-lg", bgMap[tone])}>
                {icon}
            </div>
            <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                <p className="font-bold text-xl text-slate-700 mt-1">{value}</p>
                <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
            </div>
        </div>
    );
};

const MiniMoneyBox = ({ label, value, tone }) => {
    const toneMap = {
        success: "text-green-700 bg-green-50 border-green-100",
        warning: "text-amber-700 bg-amber-50 border-amber-100",
        danger: "text-red-700 bg-red-50 border-red-100",
        primary: "text-blue-700 bg-blue-50 border-blue-100",
        muted: "text-slate-700 bg-slate-50 border-slate-200",
    };
    return (
        <div className={cx("p-4 rounded-2xl border", toneMap[tone] || toneMap.muted)}>
            <div className="text-xs opacity-80 uppercase tracking-wide">{label}</div>
            <div className="text-lg font-bold mt-2">{inr(value)}</div>
        </div>
    );
};

const ServiceIcon = ({ type }) => {
    const common = "text-lg";
    if (type === "nursing") return <FaUserNurse className={common} />;
    if (type === "physio") return <FaStethoscope className={common} />;
    if (type === "doctor") return <FaUserMd className={common} />;
    if (type === "lab") return <FaFlask className={common} />;
    return <FaHandHoldingMedical className={common} />;
};

/* =========================================================
   KPI CARDS
========================================================= */
const SparkLine = ({ series, color = "#2563eb", fill = false }) => {
    const data = (series || []).map((v, i) => ({ i, v }));
    return (
        <div className="h-14 mt-4">
            <ResponsiveContainer width="100%" height="100%">
                {fill ? (
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                                <stop offset="100%" stopColor={color} stopOpacity={0.03} />
                            </linearGradient>
                        </defs>
                        <Area
                            type="monotone"
                            dataKey="v"
                            stroke={color}
                            fill={`url(#spark-${color.replace("#", "")})`}
                            strokeWidth={2.4}
                        />
                    </AreaChart>
                ) : (
                    <LineChart data={data}>
                        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2.4} dot={false} />
                    </LineChart>
                )}
            </ResponsiveContainer>
        </div>
    );
};

const KpiShell = ({ eyebrow, title, valueNode, footer, chart, iconNode }) => (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="absolute inset-x-0 top-0 h-1 bg-blue-600" />
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-blue-600">{eyebrow}</div>
                <div className="text-sm font-semibold text-slate-700 mt-2">{title}</div>
            </div>
            <div className="h-11 w-11 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm">
                {iconNode}
            </div>
        </div>
        <div className="mt-4">{valueNode}</div>
        {chart}
        {footer ? <div className="mt-4 pt-4 border-t border-slate-100">{footer}</div> : null}
    </div>
);

const KpiPatients = ({ title, value, deltaPct, series }) => {
    const positive = Number(deltaPct) >= 0;
    return (
        <KpiShell
            eyebrow="Patients"
            title={title}
            iconNode={<FaUserFriends />}
            valueNode={
                <div className="flex items-end justify-between gap-3">
                    <div className="text-4xl font-bold tracking-tight text-slate-700">{value}</div>
                    <div
                        className={cx(
                            "text-xs px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 font-semibold",
                            positive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                        )}
                    >
                        {positive ? <FaArrowUp /> : <FaArrowDown />}
                        {Math.abs(deltaPct).toFixed(1)}%
                    </div>
                </div>
            }
            chart={<SparkLine series={series} color="#2563eb" fill />}
            footer={
                <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Compared to previous range</span>
                    <span className="font-semibold text-blue-600">Stable occupancy</span>
                </div>
            }
        />
    );
};

const KpiRevenue = ({ title, subtitle, value, series }) => (
    <KpiShell
        eyebrow="Finance"
        title={title}
        iconNode={<FaMoneyBillWave />}
        valueNode={
            <>
                <div className="text-3xl font-bold tracking-tight text-slate-700">{inr(value)}</div>
                <div className="text-xs text-slate-500 mt-2">{subtitle}</div>
            </>
        }
        chart={<SparkLine series={series} color="#2563eb" fill />}
        footer={
            <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Collection trend</span>
                <span className="font-semibold text-blue-600">Healthy growth</span>
            </div>
        }
    />
);

const KpiFeedback = ({ title, score, outOf, reviews }) => (
    <KpiShell
        eyebrow="Experience"
        title={title}
        iconNode={<FaStar />}
        valueNode={
            <>
                <div className="flex items-end gap-2">
                    <div className="text-4xl font-bold tracking-tight text-slate-700">{Number(score).toFixed(1)}</div>
                    <div className="text-slate-400 text-lg mb-1">/ {Number(outOf).toFixed(1)}</div>
                    <FaStar className="text-yellow-400 text-2xl mb-1 ml-1" />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                    Based on {Number(reviews).toLocaleString("en-IN")} verified reviews
                </div>
            </>
        }
        chart={
            <div className="mt-4">
                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                    <div className="h-2.5 bg-blue-600 rounded-full" style={{ width: `${Math.min(100, (score / outOf) * 100)}%` }} />
                </div>
            </div>
        }
        footer={
            <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Patient sentiment</span>
                <span className="font-semibold text-blue-600">Above benchmark</span>
            </div>
        }
    />
);

const KpiCompletionPulse = ({ title, pct, done, total }) => {
    const data = [{ name: "Completion", value: pct }];
    return (
        <KpiShell
            eyebrow="Operations"
            title={title}
            iconNode={<FaCheckCircle />}
            valueNode={
                <div className="mt-1 flex items-center justify-center">
                    <div className="relative w-[150px] h-[150px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadialBarChart innerRadius="78%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
                                <RadialBar dataKey="value" fill="#2563eb" cornerRadius={16} />
                            </RadialBarChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-3xl font-bold text-slate-700">{pct}%</div>
                            <div className="text-xs text-slate-500 mt-1">
                                {done} / {total}
                            </div>
                        </div>
                    </div>
                </div>
            }
            footer={
                <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Completed visits ratio</span>
                    <span className="font-semibold text-blue-600">Operationally strong</span>
                </div>
            }
        />
    );
};

export default PartnerDashboard;